"use client";

import Link from "next/link";
import Image from "next/image";
import { Icon } from "@iconify/react";
import { motion } from "motion/react";
import type { UnifiedProduct } from "@/lib/product-data/product.types";
import Badge from "./Badge";

type Props = {
  product: UnifiedProduct;
  index: number;
  basePath: string; // required now
};

export default function ProductCard({ product, index, basePath }: Props) {
  const imageSrc = product.images?.[0] ?? null;

  // Prefer subtitle then tagline so UI texts don't change unexpectedly
  const badgeText = product.subtitle ?? product.tagline ?? "";

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.06 }}
      className="group bg-white dark:bg-dark_b rounded-[2.5rem] border border-border dark:border-white/5 overflow-hidden hover:shadow-2xl transition-all duration-450 flex flex-col shadow-sm hover:-translate-y-2"
    >
      <div className="h-[350px] bg-gradient-to-br from-gray-50 to-white dark:from-white/5 dark:to-transparent relative p-8 flex items-center justify-center overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 transition-opacity duration-500 group-hover:opacity-100 opacity-0" />

        <div className="relative w-full h-full flex items-center justify-center z-10 transition-transform duration-500 group-hover:scale-105">
          {imageSrc ? (
            <Image
              src={imageSrc}
              alt={product.name}
              fill
              className="object-contain drop-shadow-xl h-auto w-auto pt-10 lg:pt-6"
              priority={index < 2}
              sizes="(max-width: 1024px) 100vw, 50vw"
            />
          ) : (
            <Icon
              icon="ion:cube-outline"
              width="120"
              className="text-muted/20"
            />
          )}
        </div>

        <Badge>{badgeText}</Badge>
      </div>

      <div className="p-8 md:p-10 flex-1 flex flex-col">
        <div className="mb-6">
          <h3 className="text-2xl font-bold text-midnight_text dark:text-white mb-2 group-hover:text-primary transition-colors">
            {product.name}
          </h3>

          <p className="text-sm font-bold text-primary tracking-wider uppercase mb-4 opacity-90">
            {product.tagline ?? product.subtitle}
          </p>

          <p className="text-muted dark:text-white/60 leading-relaxed text-sm line-clamp-3">
            {product.description}
          </p>
        </div>

        {/* Highlights mini */}
        <div className="grid grid-cols-4 gap-2 mb-8 pt-6 border-t border-border dark:border-white/5">
          {product.highlights?.slice(0, 4).map((h, i) => (
            <div
              key={i}
              className="flex flex-col items-center justify-center text-center"
            >
              <div className="w-10 h-10 rounded-xl bg-gray-50 dark:bg-white/5 text-muted dark:text-white/60 flex items-center justify-center mb-2 group-hover:text-primary transition-colors">
                <Icon icon={h.icon} width="20" />
              </div>
              <span className="text-[10px] font-bold uppercase text-muted dark:text-white/40 line-clamp-1 w-full">
                {h.title}
              </span>
            </div>
          ))}
        </div>

        <Link
          href={`${basePath}/${product.id}`}
          className="mt-auto w-full flex items-center justify-center gap-2 font-bold transition-all text-17 bg-primary text-white py-3 px-8 rounded-lg border border-primary hover:text-primary hover:bg-transparent"
        >
          View Specifications
          <Icon icon="ion:arrow-forward" width="18" />
        </Link>
      </div>
    </motion.article>
  );
}
